// src/components/common/SearchableSelect.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check, Loader2 } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ComponentType<any>;
  className?: string;
  onSearchChange?: (query: string) => void;
  /** Show a spinner (in place of the chevron) while results are refetching. */
  loading?: boolean;
  /** Value restored when the clear control is used. Defaults to empty string. */
  clearValue?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  icon: Icon,
  className = '',
  onSearchChange,
  loading = false,
  clearValue = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        onSearchChange?.('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onSearchChange]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedOption = options.find(option => option.value === value);

  const handleSelect = (option: Option) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchQuery('');
    onSearchChange?.('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(clearValue);
    setSearchQuery('');
    onSearchChange?.('');
  };

  const canClear = Boolean(selectedOption) && value !== clearValue;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Select Button — h-9 matches Input/select filter controls */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex h-9 w-full items-center rounded-md border border-input bg-background text-left text-sm shadow-sm
          focus:outline-none focus-visible:ring-1 focus-visible:ring-ring
          ${Icon ? 'pl-9' : 'pl-3'} pr-8`}
      >
        {Icon && (
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
            <Icon className="h-4 w-4 text-gray-400" />
          </span>
        )}
        <span className="block min-w-0 flex-1 truncate text-sm">
          {selectedOption ? selectedOption.label : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </span>
        {canClear && (
          <span
            className="absolute right-7 top-1/2 -translate-y-1/2 rounded-full p-0.5 hover:bg-gray-100 cursor-pointer"
            onClick={handleClear}
          >
            <X className="h-3.5 w-3.5 text-gray-400" />
          </span>
        )}
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Loading" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                className="w-full pl-8 pr-4 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  onSearchChange?.(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  className={`w-full px-4 py-2 text-sm text-left flex items-center space-x-2
                    ${option.value === value ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}
                  onClick={() => handleSelect(option)}
                >
                  <span className="w-5">
                    {option.value === value && (
                      <Check className="h-4 w-4 text-blue-500" />
                    )}
                  </span>
                  <span>{option.label}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;